import Highcharts from "highcharts/esm/highcharts";
import ExportingModule from "highcharts/esm/modules/exporting";
import ExportDataModule from "highcharts/esm/modules/export-data";
import OfflineExportingModule from "highcharts/esm/modules/offline-exporting";
import NoDataModule from "highcharts/esm/modules/no-data-to-display";
import AccessibilityModule from "highcharts/esm/modules/accessibility";

// Initialize Highcharts modules
[
  ExportingModule,
  ExportDataModule,
  OfflineExportingModule,
  NoDataModule,
  AccessibilityModule
].forEach((module: any) => {
  const init = module?.default ?? module;
  if (typeof init === "function") {
    init(Highcharts);
  }
});
Highcharts.setOptions({
  exporting: {
    libURL: '/assets/highcharts-lib/',
    fallbackToExportServer: false,

  }
});

(function (H: any) {
  // Helper: Generates a table SVG for Alert Analysis
  H.exportAlertAnalysisWithTables = function (
    charts: any[],
    options: any,
    senders: any[],
    users: any[],
    groups: any[],
    fromDate: string,
    toDate: string
  ) {
    options = H.merge(H.getOptions().exporting, options);

    const canvasWidth = 1900;
    const canvasHeight = 1350;
    const scaleFactor = 0.9;
    const leftPadding = 40;

    // Horizontal spacing constants
    const firstColumnMargin = 80;
    const itemSpacing = 100;
    const columnWidth = 500;

    const chartY = 160;
    const tableY = 650;
    const svgParts: string[] = [];

    // Header centering
    const headerCenterX = canvasWidth / 2;

    svgParts.push(`
      <g transform="translate(${headerCenterX}, 50)">
        <text x="0" y="0" class="analysis-main-title" text-anchor="middle">Alert Analysis</text>
        <text x="0" y="45" class="analysis-subtitle" text-anchor="middle">
          Report Period From: ${fromDate} Report Period To: ${toDate}
        </text>
      </g>
    `);

    // 1. Render Charts
    let currentX = firstColumnMargin;
    charts.forEach((chart) => {
      const title = chart.options?.title?.text?.toString() || "";
      if (["Alert Analysis", "Top 10 Senders", "Top 10 Group Recipients", "Top 10 User Recipients"].includes(title)) return;

      let chartSvg = chart.getSVG({
        chart: { width: columnWidth, height: 350 },
        title: { style: { fontSize: '18px', fontWeight: 'bold' } }
      });

      // Wrap in a group and place at currentX
      if (chartSvg) {
        chartSvg = chartSvg
          .replace(/<svg[^>]*>/, `<g transform="translate(${currentX}, ${chartY})">`)
          .replace("</svg>", "</g>");
        svgParts.push(chartSvg);
      }

      currentX += columnWidth + itemSpacing;
    });

    // 2. Render Tables (Aligning X exactly with Chart X)
    let currentTableX = firstColumnMargin;
    const tableConfigs = [
      { title: "Top 10 Senders", headers: ["Sender", "# of Alerts Sent"], data: senders },
      { title: "Top 10 Group Recipients", headers: ["Group", "# of Alerts Received"], data: groups },
      { title: "Top 10 User Recipients", headers: ["User", "# of Alerts Received"], data: users },
    ];

    tableConfigs.forEach((config) => {
      let tableSvg = generateTableSVG(config.title, config.headers, config.data || []);

      // FIX: Regex replace ensures we strip the nested <svg> tags so they don't fight the translate
      tableSvg = tableSvg
        .replace(/<svg[^>]*>/, `<g transform="translate(${currentTableX}, ${tableY})">`)
        .replace("</svg>", "</g>");

      svgParts.push(tableSvg);

      // Advance X by the exact same increment used for charts
      currentTableX += columnWidth + itemSpacing;
    });

    const finalSVG = `
      <svg xmlns="http://www.w3.org/2000/svg" width="${canvasWidth}" height="${canvasHeight}" viewBox="0 0 ${canvasWidth} ${canvasHeight}">
        <defs>
          <style type="text/css">
            .analysis-main-title { font-size: 26px !important; font-weight: normal !important; fill: black; }
            .analysis-subtitle { font-size: 20px !important; fill: #333; }
            .table-title { font-size: 22px !important; font-weight: bold !important; fill: black; }
            .table-header-text { font-size: 14px !important; font-weight: bold !important; fill: black; }
            .table-row-text { font-size: 13px !important; fill: black; }
          </style>
        </defs>
        <rect width="100%" height="100%" fill="white"/>
        <g transform="translate(${leftPadding}, 30) scale(${scaleFactor})">
          ${svgParts.join("")}
        </g>
      </svg>
    `;
    H.downloadSVGLocal(finalSVG, options);
  };

  function generateTableSVG(title: string, headers: string[], data: { name: string; y: number }[]) {
    const rowHeight = 30;
    const totalWidth = 500;
    const col1Width = 310;
    const col2Width = 190;
    const headerHeight = 60;
    const tableBodyHeight = Math.max(data.length * rowHeight, 100);
    const svgHeight = headerHeight + tableBodyHeight + 20;

    const dataRows = data.length
      ? data.map((row, i) => `
          <rect x="0" y="${60 + i * rowHeight}" width="${col1Width}" height="${rowHeight}" fill="none" stroke="black" stroke-width="1"/>
          <text x="10" y="${79 + i * rowHeight}" class="table-row-text">${row.name}</text>
          <rect x="${col1Width}" y="${60 + i * rowHeight}" width="${col2Width}" height="${rowHeight}" fill="none" stroke="black" stroke-width="1"/>
          <text x="${totalWidth - 15}" y="${79 + i * rowHeight}" class="table-row-text" text-anchor="end">${row.y}</text>
        `).join("")
      : `<rect x="0" y="60" width="${totalWidth}" height="100" fill="none" stroke="black" stroke-width="1"/>
           <text x="${totalWidth / 2}" y="115" class="table-row-text" text-anchor="middle">No data available</text>`;

    return `
      <svg width="${totalWidth}" height="${svgHeight}" xmlns="http://www.w3.org/2000/svg">
        <text x="${totalWidth / 2}" y="20" class="table-title" text-anchor="middle">${title}</text>
        <rect x="0" y="30" width="${col1Width}" height="30" fill="#D3D3D3" stroke="black" stroke-width="1"/>
        <rect x="${col1Width}" y="30" width="${col2Width}" height="30" fill="#D3D3D3" stroke="black" stroke-width="1"/>
        <text x="10" y="50" class="table-header-text">${headers[0]}</text>
        <text x="${totalWidth - 15}" y="50" class="table-header-text" text-anchor="end">${headers[1]}</text>
        ${dataRows}
      </svg>
    `;
  }

  // MODULE: Time To Acknowledge PDF Export
  H.exportTimeToAcknowledge = function (
    charts: any[],
    options: any,
    stats: any,
    headerInfo: { line1: string; line2: string }
  ) {
    options = H.merge(
      H.getOptions().exporting,
      { type: "application/pdf" },
      options
    );

    const canvasWidth = 1200;
    const canvasHeight = 900;
    const margin = 40;
    const availableWidth = canvasWidth - margin * 2;
    const svgParts: string[] = [];

    // 1. Header Section
    svgParts.push(`
      <g transform="translate(${canvasWidth / 2}, 25)">
        <text x="0" y="0" class="title" text-anchor="middle">Time To Acknowledge</text>
        <text x="0" y="25" class="subtitle" text-anchor="middle">${headerInfo.line1}</text>
        <text x="0" y="42" class="subtitle" text-anchor="middle">${headerInfo.line2}</text>
      </g>
    `);

    // 2. Stats Boxes - Compacted vertical position
    const boxWidth = availableWidth / 6;
    const boxHeight = 100;
    const statsData = [
      { label: "Alert Count", value: stats.alertCount },
      { label: "Ack Count", value: stats.ackCount },
      { label: "Not Ack Count", value: stats.notAckCount },
      { label: "Min Time (Hrs)", value: stats.minTime },
      { label: "Mean Time (Hrs)", value: stats.meanTime },
      { label: "Max Time (Hrs)", value: stats.maxTime },
    ];

    statsData.forEach((item, i) => {
      const x = margin + i * boxWidth;
      const y = 84;
      svgParts.push(`
        <g transform="translate(${x}, ${y})">
          <rect width="${boxWidth - 10}" height="${boxHeight}" fill="#f8f9fa" stroke="#dcdcdc" stroke-width="1"/>
          <text x="${(boxWidth - 10) / 2}" y="50" class="card-value" text-anchor="middle">${item.value}</text>
          <text x="${(boxWidth - 10) / 2}" y="75" class="card-label" text-anchor="middle">${item.label}</text>
        </g>
      `);
    });

    // 3. Main Chart
    if (charts[0]) {
      let chartSvg = charts[0].getSVG({
        chart: {
          width: availableWidth,
          height: 550,
          spacingTop: 0,
          spacingLeft: 0,
          spacingRight: 0,
          marginLeft: 60,
          marginRight: 20,
          backgroundColor: "#ffffff",
        },
        title: {
          text: "Time to Acknowledge Chart",
          style: { fontSize: '24px', fontWeight: 'normal', color: '#212529' },
        },
        exporting: {
          sourceWidth: availableWidth,
          sourceHeight: 550,
          scale: 1,
        },
      });
      if (chartSvg && typeof chartSvg === 'string') {
        chartSvg = chartSvg
          .replace("<svg", `<g transform="translate(${margin}, 195)"`)
          .replace("</svg>", "</g>");
        svgParts.push(chartSvg);
      }
    }

    // 4. Final SVG with CSS Definitions
    const finalSVG = `
      <svg xmlns="http://www.w3.org/2000/svg" 
           width="${canvasWidth}" height="${canvasHeight}" 
           viewBox="0 0 ${canvasWidth} ${canvasHeight}">
        <defs>
          <style type="text/css">
            .title { font-size: 19px !important; font-weight: normal !important; fill: #212529; }
            .subtitle { font-size: 14.5px !important; fill: #696969; }
            .card-value { font-size: 28px !important; font-weight: normal !important; fill: #212529; }
            .card-label { font-size: 13px !important; fill: #666666; }
          </style>
        </defs>
        <rect width="100%" height="100%" fill="white"/>
        ${svgParts.join("")}
      </svg>
    `;

    H.downloadSVGLocal(finalSVG, options);
  };

  // Helper: Export Table Data to CSV
  function exportTableCSV(data: any[], filename: string, headers: string[]) {
    if (!data || !data.length) return;
    const csv = [
      headers.join(","),
      ...data.map((d) => `"${d.name}","${d.y}"`),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
  }

  // Safe CSV Export for Time To Acknowledge
  H.exportTimeToAcknowledgeCSV = function (chart: Highcharts.Chart) {
    if (!chart || typeof chart.downloadCSV !== "function") return;
    setTimeout(() => {
      try {
        if (chart.container) {
          chart.downloadCSV();
        }
      } catch (err) {
        console.error("Error exporting CSV:", err);
      }
    }, 250);
  };

  // CSV Export for Alert Analysis
  H.exportAlertAnalysisCSVWithTables = function (
    charts: Highcharts.Chart[],
    filename: string,
    senders: any[],
    users: any[],
    groups: any[]
  ) {
    let delay = 0;
    charts.forEach((chart, i) => {
      if (chart && typeof chart.downloadCSV === "function") {
        const chartTitle =
          chart.options?.title?.text?.toString()?.replace(/\s+/g, "_") ||
          `Chart_${i + 1}`;
        setTimeout(() => {
          if (!chart || !chart.container) return;
          chart.update({ exporting: { filename: `${chartTitle}` } }, false);
          chart.downloadCSV();
        }, delay);
        delay += 300;
      }
    });

    setTimeout(() => {
      exportTableCSV(senders, `top_10_Senders.csv`, [
        "Sender",
        "# of Alerts Sent",
      ]);
      exportTableCSV(groups, `top_10_Groups.csv`, [
        "Group",
        "# of Alerts Received",
      ]);
      exportTableCSV(users, `top_10_Users.csv`, [
        "User",
        "# of Alerts Received",
      ]);
    }, delay + 300);
  };

  // MODULE: Time To Resolve PDF Export
  H.exportTimeToResolve = function (
    charts: any[],
    options: any,
    stats: any,
    headerInfo: { line1: string; line2: string }
  ) {
    options = H.merge(
      H.getOptions().exporting,
      { type: "application/pdf" },
      options
    );

    const canvasWidth = 1200;
    const canvasHeight = 900;
    const margin = 40;
    const availableWidth = canvasWidth - margin * 2;
    const svgParts: string[] = [];

    // 1. Header Section
    svgParts.push(`
    <g transform="translate(${canvasWidth / 2}, 25)">
      <text x="0" y="0" class="title" text-anchor="middle">Time To Resolve</text>
      <text x="0" y="25" class="subtitle" text-anchor="middle">${headerInfo.line1}</text>
      <text x="0" y="42" class="subtitle" text-anchor="middle">${headerInfo.line2}</text>
    </g>
  `);

    // 2. Stats Boxes
    const boxWidth = availableWidth / 6;
    const boxHeight = 100;
    const statsData = [
      { label: "Alert Count", value: stats.alertCount },
      { label: "Res Count", value: stats.resCount },
      { label: "Not Res Count", value: stats.notResCount },
      { label: "Min Time (Hrs)", value: stats.minTime },
      { label: "Mean Time (Hrs)", value: stats.meanTime },
      { label: "Max Time (Hrs)", value: stats.maxTime },
    ];

    statsData.forEach((item, i) => {
      const x = margin + i * boxWidth;
      const y = 85;
      svgParts.push(`
      <g transform="translate(${x}, ${y})">
        <rect width="${boxWidth - 10}" height="${boxHeight}" fill="#f8f9fa" stroke="#dcdcdc" stroke-width="1"/>
        <text x="${(boxWidth - 10) / 2}" y="50" class="card-value" text-anchor="middle">${item.value}</text>
        <text x="${(boxWidth - 10) / 2}" y="75" class="card-label" text-anchor="middle">${item.label}</text>
      </g>
    `);
    });

    // 3. Main Chart
    if (charts[0]) {
      let chartSvg = charts[0].getSVG({
        chart: {
          width: availableWidth,
          height: 550,
          spacingTop: 0,
          spacingLeft: 0,
          spacingRight: 0,
          marginLeft: 60,
          marginRight: 60,
          backgroundColor: "#ffffff",
        },
        title: {
          text: "Time to Resolve Chart",
          style: { fontSize: '24px', fontWeight: 'normal', color: '#212529' }
        },
        exporting: {
          sourceWidth: availableWidth,
          sourceHeight: 550,
          scale: 1,
        },
      });

      // Fixed: Positioned tightly under the cards
      chartSvg = chartSvg
        .replace("<svg", `<g transform="translate(${margin}, 200)"`)
        .replace("</svg>", "</g>");
      svgParts.push(chartSvg);
    }

    // 4. Final SVG with CSS Definitions
    const finalSVG = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${canvasWidth}" height="${canvasHeight}" viewBox="0 0 ${canvasWidth} ${canvasHeight}">
      <defs>
        <style type="text/css">
          .title { font-size: 19px !important; font-weight: normal !important; fill: #212529; }
          .subtitle { font-size: 14.5px !important; fill: #696969; }
          .card-value { font-size: 32px !important; font-weight: normal !important; fill: #212529; }
          .card-label { font-size: 13px !important; fill: #666666; }
        </style>
      </defs>
      <rect width="100%" height="100%" fill="white"/>
      ${svgParts.join("")}
    </svg>
  `;

    H.downloadSVGLocal(finalSVG, options);
  };

  // MODULE: Open Alert PDF Export
  H.exportOpenAlerts = function (
    charts: any[],
    options: any,
    headerInfo: { title: string; subtitle: string }
  ) {
    options = H.merge(
      H.getOptions().exporting,
      { type: "application/pdf" },
      options
    );

    const canvasWidth = 1450;
    const canvasHeight = 1100;
    const margin = 40;
    const columns = 3;
    const containerWidth = 450;
    const containerHeight = 280;
    const chartWidth = 410;
    const chartHeight = 240;
    const horizontalSpacing = 15;
    const verticalSpacing = 15;

    // FIX: Reduced top spacing to remove the big white gap
    const topOffset = 80;

    const svgParts: string[] = [];

    // Main Page Header
    svgParts.push(`
    <g transform="translate(${canvasWidth / 2}, 35)">
      <text x="0" y="0" class="title" text-anchor="middle">${headerInfo.title}</text>
      <text x="0" y="22" class="subtitle" text-anchor="middle">${headerInfo.subtitle}</text>
    </g>
  `);

    charts.forEach((chart, index) => {
      if (!chart || typeof chart.getSVG !== "function") return;

      const row = Math.floor(index / columns);
      const col = index % columns;
      const xPos = margin + col * (containerWidth + horizontalSpacing);
      const yPos = topOffset + row * (containerHeight + verticalSpacing);

      const internalX = (containerWidth - chartWidth) / 2;
      const internalY = (containerHeight - chartHeight) / 2;

      let chartSvg = chart.getSVG({
        chart: {
          width: chartWidth,
          height: chartHeight,
          backgroundColor: "transparent",
          spacingTop: 15 // Gives the title a bit of breathing room from the top border
        },
        // --- THE SPECIFIC FIX FOR CHART TITLES ---
        title: {
          text: chart.options.title.text,
          style: {
            fontSize: '18px',      // Larger than dev
            fontWeight: 'normal',  // Normal weight, NOT bold
            color: '#333333'
          }
        },
        // -----------------------------------------
        exporting: {
          sourceWidth: chartWidth,
          sourceHeight: chartHeight,
          scale: 1
        }
      });

      if (chartSvg && typeof chartSvg === "string") {
        const borderSvg = `<rect x="0" y="0" width="${containerWidth}" height="${containerHeight}" fill="none" stroke="#dcdcdc" stroke-width="0.5"/>`;

        const processedSvg = chartSvg
          .replace(/<svg[^>]*>/, `<g transform="translate(${internalX}, ${internalY})">`)
          .replace("</svg>", "</g>");

        svgParts.push(`
        <g transform="translate(${xPos}, ${yPos})">
          ${borderSvg}
          ${processedSvg}
        </g>
      `);
      }
    });

    const finalSVG = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${canvasWidth}" height="${canvasHeight}" viewBox="0 0 ${canvasWidth} ${canvasHeight}">
      <defs>
        <style type="text/css">
          .title { font-size: 22px !important; font-weight: normal !important; fill: #212529; font-family: "Times New Roman", serif; }
          .subtitle { font-size: 14px !important; fill: #666666; font-family: serif; }
        </style>
      </defs>
      <rect width="100%" height="100%" fill="white"/>
      <g transform="translate(0,0)">
        ${svgParts.join("")}
      </g>
    </svg>
  `;

    H.downloadSVGLocal(finalSVG, options);
  };

  // MODULE: Alert Trend PDF Export
  H.exportAlertTrend = function (
    charts: any[],
    options: any,
    headerInfo: {
      title: string;
      level: string;
      priority: string;
      group: string;
      integration: string;
      topic: string;
    }
  ) {
    options = H.merge(
      H.getOptions().exporting,
      { type: "application/pdf" },
      options
    );

    // 1. Layout Dimensions
    const canvasWidth = 1250;
    const canvasHeight = 1100;
    const margin = 40;
    const columns = 2;

    // The outer bordered box
    const containerWidth = 560;
    const containerHeight = 350;

    // The internal chart size 
    const chartWidth = 520;
    const chartHeight = 310;

    const horizontalSpacing = 20;
    const verticalSpacing = 20;

    const topOffset = 100;

    const svgParts: string[] = [];

    // 2. Header Section
    svgParts.push(`
    <g transform="translate(${canvasWidth / 2}, 35)">
      <text x="0" y="0" class="title" text-anchor="middle">${headerInfo.title}</text>
      <text x="0" y="25" class="subtitle" text-anchor="middle">
        Report Level: ${headerInfo.level}, Priority: ${headerInfo.priority}, Group: ${headerInfo.group}
      </text>
      <text x="0" y="42" class="subtitle" text-anchor="middle">
        Integration: ${headerInfo.integration}, Topic: ${headerInfo.topic}
      </text>
    </g>
  `);

    // 3. Grid Loop
    charts.forEach((chart, index) => {
      if (!chart || typeof chart.getSVG !== "function") return;

      const row = Math.floor(index / columns);
      const col = index % columns;
      const xPos = margin + col * (containerWidth + horizontalSpacing);
      const yPos = topOffset + row * (containerHeight + verticalSpacing);

      // Centering math
      const internalX = (containerWidth - chartWidth) / 2;
      const internalY = (containerHeight - chartHeight) / 2;

      let chartSvg = chart.getSVG({
        chart: {
          width: chartWidth,
          height: chartHeight,
          backgroundColor: "transparent",
          spacingTop: 15
        },
        // FIX: Apply the "Stage" Title Style (Larger, Not Bold)
        title: {
          text: chart.options.title.text,
          style: {
            fontSize: '20px',
            fontWeight: 'normal',
            color: '#333333'
          }
        },
        exporting: {
          sourceWidth: chartWidth,
          sourceHeight: chartHeight,
          scale: 1
        }
      });

      if (chartSvg && typeof chartSvg === "string") {
        // Create clean border first
        const borderSvg = `<rect x="0" y="0" width="${containerWidth}" height="${containerHeight}" fill="none" stroke="#dcdcdc" stroke-width="0.5"/>`;

        // Wrap chart in a group and shift it to the center of the border
        const processedSvg = chartSvg
          .replace(/<svg[^>]*>/, `<g transform="translate(${internalX}, ${internalY})">`)
          .replace("</svg>", "</g>");

        svgParts.push(`
        <g transform="translate(${xPos}, ${yPos})">
          ${borderSvg}
          ${processedSvg}
        </g>
      `);
      }
    });

    const finalSVG = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${canvasWidth}" height="${canvasHeight}" viewBox="0 0 ${canvasWidth} ${canvasHeight}">
      <defs>
        <style type="text/css">
          .title { font-size: 22px !important; font-weight: normal !important; fill: #212529; font-family: "Times New Roman", serif; }
          .subtitle { font-size: 14px !important; fill: #666666; }
        </style>
      </defs>
      <rect width="100%" height="100%" fill="white"/>
      ${svgParts.join("")}
    </svg>
  `;

    H.downloadSVGLocal(finalSVG, options);
  };

  // MODULE: Alert Summary PDF Export
  H.exportAlertSummary = function (charts, options, headerInfo) {
    options = H.merge(
      H.getOptions().exporting,
      { type: "application/pdf" },
      options
    );

    const canvasWidth = 1200;
    const canvasHeight = 1100;
    const margin = 40;
    const columns = 2;

    const containerWidth = 540;
    const containerHeight = 280;
    const chartWidth = 500;
    const chartHeight = 240;

    const horizontalSpacing = 20;
    const verticalSpacing = 15;

    const topOffset = 110;

    const svgParts = [];

    // 1. Header Section
    svgParts.push(`
    <g transform="translate(${canvasWidth / 2}, 35)">
      <text x="0" y="0" class="title" text-anchor="middle">${headerInfo.title}</text>
      
      <text x="0" y="28" class="subtitle" text-anchor="middle">
        ${headerInfo.period}, Priority: ${headerInfo.priority}, Group: ${headerInfo.group}
      </text>
      <text x="0" y="46" class="subtitle" text-anchor="middle">
        Integration: ${headerInfo.integration}, Topic: ${headerInfo.topic}
      </text>
    </g>
  `);

    // 2. Grid Loop
    charts.forEach((chart, index) => {
      if (!chart || typeof chart.getSVG !== "function") return;

      const row = Math.floor(index / columns);
      const col = index % columns;
      const xPos = margin + col * (containerWidth + horizontalSpacing);
      const yPos = topOffset + row * (containerHeight + verticalSpacing);

      const internalX = (containerWidth - chartWidth) / 2;
      const internalY = (containerHeight - chartHeight) / 2;

      let chartSvg = chart.getSVG({
        chart: {
          width: chartWidth,
          height: chartHeight,
          backgroundColor: "#ffffff",
        },
        title: { text: chart.options.title.text, style: { fontSize: '18px' } },
        exporting: {
          enabled: false,
          sourceWidth: chartWidth,
          sourceHeight: chartHeight,
          scale: 1,
        },
      });

      if (chartSvg && typeof chartSvg === "string") {
        const borderSvg = `<rect x="0" y="0" width="${containerWidth}" height="${containerHeight}" fill="none" stroke="#dcdcdc" stroke-width="0.5"/>`;

        const processedSvg = chartSvg
          .replace(
            /<svg[^>]*>/,
            `<g transform="translate(${xPos}, ${yPos})">
             ${borderSvg}
             <g transform="translate(${internalX}, ${internalY})">`
          )
          .replace("</svg>", "</g></g>");

        svgParts.push(processedSvg);
      }
    });

    // 3. Final SVG Assembly
    const finalSVG = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${canvasWidth}" height="${canvasHeight}" viewBox="0 0 ${canvasWidth} ${canvasHeight}">
      <defs>
        <style type="text/css">
          .title { 
            font-size: 19px !important; 
            font-weight: normal !important; 
            fill: #212529; 
          }
          .subtitle { 
            font-size: 14px !important; 
            fill: #696969; 
          }
        </style>
      </defs>
      <rect width="100%" height="100%" fill="white"/>
      ${svgParts.join("")}
    </svg>
  `;

    H.downloadSVGLocal(finalSVG, options);
  };

  H.exportEscalationPolicyPDF = function (
    charts: any[],
    options: any,
    senders: any[],
    users: any[],
    fromDate: string,
    toDate: string,
    policyName: string
  ) {
    options = H.merge(H.getOptions().exporting, options);

    const canvasWidth = 1900;
    const canvasHeight = 1350;
    const scaleFactor = 0.9;
    const leftPadding = 40;

    // Horizontal spacing constants
    const firstColumnMargin = 80;
    const itemSpacing = 100;
    const columnWidth = 500;

    // Rows Y positions
    const row1Y = 160;
    const row2Y = 600;
    const row3Y = 1000;

    const svgParts: string[] = [];

    // Header centering
    const headerCenterX = canvasWidth / 2;

    svgParts.push(`
      <g transform="translate(${headerCenterX}, 50)">
        <text x="0" y="0" class="analysis-main-title" text-anchor="middle">Alert Analysis</text>
        <text x="0" y="45" class="analysis-subtitle" text-anchor="middle">
          Report Period From: ${fromDate} Report Period To: ${toDate}
        </text>
      </g>
    `);

    // Chart Mapping for Escalation Policy
    const row1Titles = ["Deliveries Reach", "Delivery Types", "Top 5 Alert Reach"];
    const row2Titles = ["Top 10 Topics Reach", "Top 10 Group Recipients With No Topic"];
    const row3Titles = ["Top 10 Senders", "Top 10 User Recipients"];

    let row1X = firstColumnMargin;
    let row2X = firstColumnMargin;
    let row3X = firstColumnMargin;

    charts.forEach((chart) => {
      if (!chart || !chart.container) return; // Guard against destroyed charts

      const title = chart.options?.title?.text?.toString() || "";
      let currentX = 0;
      let currentY = 0;
      let width = columnWidth;
      let height = 350;

      if (row1Titles.includes(title)) {
        currentX = row1X;
        currentY = row1Y;
        row1X += columnWidth + itemSpacing;
      } else if (row2Titles.includes(title)) {
        currentX = row2X;
        currentY = row2Y;
        width = 800; // Wider charts
        row2X += 800 + itemSpacing;
      } else if (row3Titles.includes(title)) {
        currentX = row3X;
        currentY = row3Y;
        width = 800; // Wider charts
        row3X += 800 + itemSpacing;
      } else {
        return; // Skip other charts if any
      }

      let chartSvg = chart.getSVG({
        chart: { width: width, height: height },
        title: { style: { fontSize: '18px', fontWeight: 'bold' } }
      });

      if (chartSvg) {
        chartSvg = chartSvg
          .replace(/<svg[^>]*>/, `<g transform="translate(${currentX}, ${currentY})">`)
          .replace("</svg>", "</g>");
        svgParts.push(chartSvg);
      }
    });

    const finalSVG = `
      <svg xmlns="http://www.w3.org/2000/svg" width="${canvasWidth}" height="${canvasHeight}" viewBox="0 0 ${canvasWidth} ${canvasHeight}">
        <defs>
          <style type="text/css">
            .analysis-main-title { font-size: 26px !important; font-weight: normal !important; fill: black; }
            .analysis-subtitle { font-size: 20px !important; fill: #333; }
            .table-title { font-size: 22px !important; font-weight: bold !important; fill: black; }
            .table-header-text { font-size: 14px !important; font-weight: bold !important; fill: black; }
            .table-row-text { font-size: 13px !important; fill: black; }
          </style>
        </defs>
        <rect width="100%" height="100%" fill="white"/>
        <g transform="translate(${leftPadding}, 30) scale(${scaleFactor})">
          ${svgParts.join("")}
        </g>
      </svg>
    `;
    H.downloadSVGLocal(finalSVG, options);
  };
})(Highcharts);

export default Highcharts;
